/**
 * Site Permissions Service
 * Provides API methods for managing site permissions for content managers
 */

import { authFetch, handleResponse } from '@/utils/auth-fetch';

// ============================================================================
// Types
// ============================================================================

export interface SitePermission {
  id: string;
  user_id: string;
  site_id: string;
  granted_by: string;
  granted_at: string;
  user_name?: string;
  user_email?: string;
  site_name?: string;
  granted_by_name?: string;
}

export interface GrantPermissionRequest {
  userId: string;
  siteId: string;
}

export interface SitePermissionsResponse {
  success: boolean;
  permissions?: SitePermission[];
  permission?: SitePermission;
  message?: string;
  error?: string;
}

// ============================================================================
// Site Permissions Service
// ============================================================================

class SitePermissionsService {
  /**
   * Grant site permission to a user
   * Super admin only
   */
  async grantPermission(
    userId: string,
    siteId: string
  ): Promise<SitePermissionsResponse> {
    const response = await authFetch('/api/site-permissions', {
      method: 'POST',
      body: JSON.stringify({ userId, siteId }),
    });

    return handleResponse<SitePermissionsResponse>(response);
  }

  /**
   * Revoke site permission from a user
   * Super admin only
   */
  async revokePermission(
    userId: string,
    siteId: string
  ): Promise<SitePermissionsResponse> {
    const response = await authFetch(
      `/api/site-permissions/${userId}/${siteId}`,
      {
        method: 'DELETE',
      }
    );

    return handleResponse<SitePermissionsResponse>(response);
  }

  /**
   * Get all sites a user has permission to manage
   */
  async getUserSites(userId: string): Promise<SitePermission[]> {
    const response = await authFetch(`/api/site-permissions/user/${userId}`);

    const data = await handleResponse<SitePermissionsResponse>(response);
    return data.permissions || [];
  }

  /**
   * Get all users who have permission to manage a site
   * Super admin only
   */
  async getSiteUsers(siteId: string): Promise<SitePermission[]> {
    const response = await authFetch(`/api/site-permissions/site/${siteId}`);

    const data = await handleResponse<SitePermissionsResponse>(response);
    return data.permissions || [];
  }

  /**
   * Check if a user has permission to manage a specific site
   * This is a client-side helper that fetches user's sites and checks
   */
  async hasPermission(userId: string, siteId: string): Promise<boolean> {
    try {
      const permissions = await this.getUserSites(userId);
      return permissions.some((p) => p.site_id === siteId);
    } catch (error) {
      console.error('Error checking site permission:', error);
      return false;
    }
  }
}

// Export singleton instance
export const sitePermissionsService = new SitePermissionsService();

export default sitePermissionsService;
